import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../components/ui/button";
import { X, ChevronRight, LayoutGrid, BarChart3, Share2, Zap } from "lucide-react";

/**
 * WelcomeTour — a lightweight 3-step highlight tour.
 *
 * Props:
 *  - open: boolean — tour is active
 *  - onClose: () => void — called when user finishes or skips
 *  - steps: Array<{ testid, title, body, Icon }>
 */
export default function WelcomeTour({ open, onClose, steps }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const scrolledFor = useRef(null);

  const step = steps?.[idx];

  // Recompute target rect on step change + on resize/scroll.
  useLayoutEffect(() => {
    if (!open || !step) return;
    const compute = () => {
      const el = document.querySelector(`[data-testid="${step.testid}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      // Scroll into view once per step so the user sees the highlight.
      if (scrolledFor.current !== step.testid) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        scrolledFor.current = step.testid;
      }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
    };
    compute();
    const t = setTimeout(compute, 350); // after smooth-scroll settles
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, step]);

  // Keyboard: Esc to close, Enter/→ to advance.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      else if (e.key === "Enter" || e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx]);

  const handleClose = () => {
    setIdx(0);
    scrolledFor.current = null;
    onClose?.();
  };

  const handleNext = () => {
    if (idx + 1 >= (steps?.length || 0)) {
      handleClose();
    } else {
      setIdx((n) => n + 1);
    }
  };

  if (!open || !step) return null;

  // Fallback: if target not found, show centered card.
  const PAD = 10;
  const spot = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  // Position the tooltip under the spotlight (or above if no room).
  const TOOLTIP_W = 340;
  let tipStyle = {};
  if (spot) {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const below = spot.top + spot.height + 14;
    const spaceBelow = vh - below;
    const useAbove = spaceBelow < 220 && spot.top > 220;
    const tipTop = useAbove ? spot.top - 220 - 14 : below;
    let tipLeft = spot.left + spot.width / 2 - TOOLTIP_W / 2;
    tipLeft = Math.max(16, Math.min(vw - TOOLTIP_W - 16, tipLeft));
    tipStyle = { top: tipTop, left: tipLeft, width: TOOLTIP_W };
  } else {
    tipStyle = {
      top: "50%",
      left: "50%",
      width: TOOLTIP_W,
      transform: "translate(-50%, -50%)",
    };
  }

  const StepIcon = step.Icon;
  const total = steps.length;

  return createPortal(
    <div
      className="fixed inset-0 z-[70]"
      data-testid="welcome-tour"
      aria-live="polite"
    >
      {/* Dim backdrop. Clicking it advances to the next step. */}
      <div
        className="absolute inset-0 bg-black/55 transition-opacity"
        onClick={handleNext}
        data-testid="tour-backdrop"
      />

      {/* Spotlight — a transparent "hole" using an outer box-shadow trick wouldn't
          allow clicks through on the target. We instead render the backdrop + a
          highlighted outline rectangle at the target's position. */}
      {spot && (
        <div
          className="absolute rounded-xl ring-2 ring-[#D99C3D] shadow-[0_0_0_4px_rgba(217,156,61,0.25)] pointer-events-none transition-all duration-200"
          style={{
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
          }}
          data-testid="tour-spotlight"
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-[#FDFCFA] rounded-2xl shadow-2xl p-5 border border-[#1A362D]/10"
        style={tipStyle}
        data-testid={`tour-step-${idx}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#1A362D] text-[#F4F1EE] flex items-center justify-center">
              {StepIcon ? <StepIcon className="w-4 h-4" /> : null}
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">
              Step {idx + 1} of {total}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-[#5C6B64] hover:text-[#1A362D] transition-colors"
            aria-label="Close tour"
            data-testid="tour-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="font-display text-xl text-[#1A362D] mt-3">{step.title}</h3>
        <p className="text-sm text-[#5C6B64] mt-1.5 leading-relaxed">{step.body}</p>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleClose}
            className="text-xs text-[#5C6B64] hover:text-[#1A362D] underline"
            data-testid="tour-skip-btn"
          >
            Skip tour
          </button>
          <Button
            onClick={handleNext}
            className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-5"
            data-testid="tour-next-btn"
          >
            {idx + 1 >= total ? "Done" : (
              <>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mt-4">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-[#D99C3D]" : "w-1.5 bg-[#D4CFC5]"
              }`}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Convenience: canonical 3-step tour used after first resume import.
export const DEFAULT_TOUR_STEPS = [
  {
    testid: "view-kanban-btn",
    title: "See it as a Kanban board",
    body: "Switch to Kanban to drag applications between Applied, Interviewing, Offer, and Rejected — great for a visual-first pipeline.",
    Icon: LayoutGrid,
  },
  {
    testid: "nav-analytics-link",
    title: "Measure your search",
    body: "Analytics shows your funnel, response times, and which sources convert best. Spot where to focus next.",
    Icon: BarChart3,
  },
  {
    testid: "share-btn",
    title: "Share a snapshot",
    body: "Generate a read-only link to send to a mentor, coach, or recruiter — no login required on their side.",
    Icon: Share2,
  },
];

// "You're getting serious" tour — fires once when a free user crosses ~20 apps.
// Highlights the Pro-locked surfaces so the moment they feel invested, they see
// exactly what's waiting on the other side of upgrading.
export const PRO_UNLOCK_TOUR_STEPS = [
  {
    testid: "view-kanban-btn",
    title: "Unlock Kanban drag-and-drop",
    body: "You're deep in the search now. Pro turns this view into a tactile board — drag cards between stages, see your pipeline at a glance.",
    Icon: LayoutGrid,
  },
  {
    testid: "nav-analytics-link",
    title: "See which applications convert",
    body: "Pro Analytics breaks down your funnel, response times, and which sources actually land interviews. Your next move, informed.",
    Icon: BarChart3,
  },
  {
    testid: "header-upgrade-btn",
    title: "$6/mo or $19 lifetime",
    body: "Keep the free 25 forever, or unlock unlimited + Kanban + Analytics + Excel & PDF exports + AI parse. Takes 20 seconds.",
    Icon: Zap,
  },
];
